import type { AxiosError, AxiosResponse } from 'axios';
import type { CoreApiError } from '@lqa-frontend/api-services';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import {
	coreApiClient,
	coreApiQueryKey,
	endpoints,
} from '@lqa-frontend/api-services';

/**
 * GET /v2/roster — paginated list of consultants' rosters for a given month.
 * Bearer token is attached automatically by the axios interceptor in
 * `client.ts`. Backend DTO whitelists only the params declared here; adding
 * unknown fields will trigger a 400.
 */
export interface RostersQueryParams {
	page: number;
	pageSize: number;
	sort?: string;
	month: number;
	year: number;
	status?: string;
	consultantId?: number;
}

export interface RosterConsultant {
	id: number;
	userFirstName: string;
	userLastName: string;
}

export interface RosterEntry {
	id: number;
	status: string;
	consultantId: number;
	consultant: RosterConsultant;
	noOfWeekends: number;
	noOfTravelDays: number;
	noOfRestDays: number;
	noOfSickDays: number;
	noOfPublicHolidays: number;
	noOfHolidays: number;
	noOfWorkFromHome: number;
	noOfAdditionalAssessments: number;
	noOfPlanningDays: number;
	noOfUpfrontPrep: number;
	noOfInLieu: number;
	noOfOfficeDay: number;
	totalNoOfOfficeDay: number;
	noOfAssessmentNights: number;
	totalNoOfAssessmentNights: number;
	totalNoOfAdditionalAssessments: number;
	noOfArriveHome: number;
	noOfCheckoutAndFly: number;
	noOfTrainingDay: number;
	noOfOther: number;
	noOfDayOff: number;
}

export interface RostersResponse {
	results: RosterEntry[];
	total: number;
	pageSize: number;
	numberOfPages: number;
}

export type RostersQueryOptions = Omit<
	UseQueryOptions<
		AxiosResponse<RostersResponse>,
		AxiosError<CoreApiError>,
		RostersResponse
	>,
	'queryKey' | 'queryFn' | 'select'
>;

export function useRostersQuery(
	params: RostersQueryParams,
	options?: RostersQueryOptions
) {
	return useQuery<
		AxiosResponse<RostersResponse>,
		AxiosError<CoreApiError>,
		RostersResponse
	>({
		queryKey: coreApiQueryKey(endpoints.roster.index, 'get', params),
		queryFn: async () =>
			coreApiClient.get<RostersResponse>(endpoints.roster.index, { params }),
		...options,
		select: (response) => response.data,
	});
}

/**
 * One International SOS flight the consultant registered on their flight
 * planner, as `GET /v2/roster/{rosterId}` attaches it to a day.
 *
 * The backend decides which day a flight belongs to — the day whose date
 * equals the flight's origin calendar day — so the frontend only renders
 * what it is given.
 *
 * Dates arrive as ISO strings on the wire; the response interceptor in
 * `client.ts` turns them into `Date` instances, so formatters must handle
 * both (see `RosterLogEntry`).
 *
 * A flight can end on the next calendar day (an overnight leg), so the
 * destination date is not necessarily the day's own date.
 */
export interface RosterInternationalSosFlight {
	origin: string;
	originDate: string;
	destination: string;
	destinationDate: string;
	/** e.g. `EK`. Carried by the payload; the day card doesn't show it. */
	airlineCode: string;
	/** e.g. `201`. Carried by the payload; the day card doesn't show it. */
	flightNumber: string;
}

/**
 * Single day-entry inside `RosterDetailResponse.details[]`.
 *
 * `task` carries the activity type as a string and is the only thing
 * that says what a day is. `assessmentId` being set marks an assessment
 * day (and `task` is `null` in that case).
 *
 * `additionalDetails` is consultant-entered free text and never changes
 * the classification — `task: "other"` with `additionalDetails: "Day
 * Off"` is an `other` day, because that is how the BE counts it. Reading
 * the text as a classification is what used to make the activity tiles
 * disagree with the day list.
 */
export interface RosterDetailDay {
	id: number;
	rosterId: number;
	date: string;
	dayName: string;
	task: string | null;
	assessmentId: number | null;
	inquiryType: string | null;
	isFeedbackPlus: boolean | null;
	isShadow: boolean | null;
	/**
	 * Flight costs for the day. Nullable `integer` columns on
	 * `roster_details`, so normally a number or `null` — and the
	 * endpoint also sends the text `"N/A"` for a cost that does not
	 * apply. The roster shows a figure it can read and `"N/A"` for
	 * everything else, `null` included. See
	 * `data/day/assessment-flight-costs.ts`.
	 */
	flightCostPartner: number | string | null;
	flightCostConsultant: number | string | null;
	notes: string | null;
	bookingName: string | null;
	additionalDetails: string;
	flightPlannerId: number | null;
	taskText: string | null;
	contractNotes: string;
	consultantNotes: string;
	internalNotes: string;
	/**
	 * International SOS flights whose origin day is this day; `[]` when
	 * the consultant registered none. Optional because a backend that
	 * predates the field omits it entirely.
	 */
	internationalSosFlights?: RosterInternationalSosFlight[];
}

/**
 * Shape of `GET /v2/roster/{rosterId}`. The endpoint does *not* return
 * pre-aggregated `noOf*` counts — consumers must bucket the `details[]`
 * array by `task` (with the special-cases described on `RosterDetailDay`).
 */
export interface RosterDetailResponse {
	id: number;
	status: string;
	submittedAt: string | null;
	consultant: RosterConsultant;
	details: RosterDetailDay[];
	adminNotes: string;
	consultantNotes: string | null;
}

/**
 * GET /v2/roster/{rosterId} — full detail for a single roster. Pass `null`
 * for `rosterId` to disable the query (e.g. when no row is selected).
 * Generic so callers can substitute a stricter type when the contract
 * firms up.
 */
export type RosterDetailQueryOptions<T> = Omit<
	UseQueryOptions<AxiosResponse<T>, AxiosError<CoreApiError>, T>,
	'queryKey' | 'queryFn' | 'select' | 'enabled'
>;

export function useRosterDetailQuery<T = RosterDetailResponse>(
	rosterId: number | null,
	options?: RosterDetailQueryOptions<T>
) {
	return useQuery<AxiosResponse<T>, AxiosError<CoreApiError>, T>({
		queryKey: coreApiQueryKey(endpoints.roster.detail(0), 'get', rosterId),
		queryFn: async () =>
			coreApiClient.get<T>(endpoints.roster.detail(rosterId as number)),
		enabled: rosterId != null,
		...options,
		select: (response) => response.data,
	});
}

/**
 * GET /v2/roster/{consultantId}/overview?month=&year= — lightweight lookup
 * that returns the `rosterId` (and a status snapshot) for a given consultant
 * + month combination. Used by the detail drawer's month-switcher to find
 * the rosterId for a different month without leaving the drawer.
 */
export interface RosterOverviewParams {
	consultantId: number;
	month: number;
	year: number;
}

export interface RosterOverviewResponse {
	consultant?: RosterConsultant;
	rosterId: number | null;
	status: string;
	month: number;
	year: number;
	// Per-activity counts. The endpoint returns the full set in one shot
	// — these power the FilterColumn in the detail drawer's Overview tab.
	noOfAdditionalAssessments?: number;
	totalNoOfAdditionalAssessments?: number;
	noOfAssessmentNights?: number;
	totalNoOfAssessmentNights?: number;
	noOfOfficeDay?: number;
	totalNoOfOfficeDay?: number;
	noOfHolidays?: number;
	noOfInLieu?: number;
	noOfPlanningDays?: number;
	noOfRestDays?: number;
	noOfSickDays?: number;
	noOfTravelDays?: number;
	noOfUpfrontPrep?: number;
	noOfWeekends?: number;
	noOfWorkFromHome?: number;
	noOfOther?: number;
	noOfPublicHolidays?: number;
	noOfArriveHome?: number;
	noOfCheckoutAndFly?: number;
	noOfTrainingDay?: number;
	noOfNoActivityAdded?: number;
	noOfDayOff?: number;
}

export function useRosterOverviewQuery(params: RosterOverviewParams | null) {
	return useQuery<
		AxiosResponse<RosterOverviewResponse>,
		AxiosError<CoreApiError>,
		RosterOverviewResponse
	>({
		queryKey: coreApiQueryKey(endpoints.roster.overview(0), 'get', params),
		queryFn: async () =>
			coreApiClient.get<RosterOverviewResponse>(
				endpoints.roster.overview(params!.consultantId),
				{ params: { month: params!.month, year: params!.year } }
			),
		enabled: params != null,
		select: (response) => response.data,
	});
}

/**
 * One month cell in the roster calendar payload used by
 * `YearMonthStatusPicker` (status dot colour per month).
 */
export interface RosterCalendarMonth {
	year: number;
	/** 1–12 */
	month: number;
	/** API roster status string; `null` when no roster exists for that month. */
	status: string | null;
	/** Roster record id for that month; `null` when no roster exists. */
	rosterId: number | null;
}

/**
 * GET /v2/roster/{consultantId}/statuses — all months the picker needs in
 * one response (replaces 12× `/overview` calls per browsed year).
 */
export interface RosterCalendarResponse {
	consultantId: number;
	months: RosterCalendarMonth[];
}

/** Builds the `YYYY-MM` lookup key used by the month-status picker. */
export function rosterCalendarMonthKey(year: number, month: number): string {
	return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * GET /v2/roster/{consultantId}/statuses — populates `YearMonthStatusPicker`
 * status dots in a single request.
 */
export function useRosterCalendarQuery(consultantId: number | null) {
	return useQuery<
		AxiosResponse<RosterCalendarResponse>,
		AxiosError<CoreApiError>,
		RosterCalendarResponse
	>({
		queryKey: coreApiQueryKey(
			endpoints.roster.statuses(0),
			'get',
			consultantId
		),
		queryFn: async () =>
			coreApiClient.get<RosterCalendarResponse>(
				endpoints.roster.statuses(consultantId as number)
			),
		enabled: consultantId != null,
		staleTime: 5 * 60 * 1000,
		select: (response) => response.data,
	});
}

// ─────────────────────────────────────────────────────────────────────────────
// Roster log (audit trail)
// ─────────────────────────────────────────────────────────────────────────────

export type RosterLogStatus =
	'approved' | 'denied' | 'submitted' | 'inProgress';

export interface RosterLogAdmin {
	userFirstName: string;
	userLastName: string;
	userEmail: string;
}

/**
 * Single audit-log entry from `GET /v2/roster/{rosterId}/logs`.
 *
 * - Endpoint requires LQA-Admin auth + the `isNewAdminRosterEnabled` feature
 *   flag (otherwise 403). 401 anonymous, 404 unknown rosterId.
 * - Response is a JSON **array**, ordered newest-first by
 *   `insertDate ?? updateDate` (insertDate is the action time).
 * - Date fields arrive as ISO strings on the wire; the axios response
 *   interceptor (see `client.ts`) converts ISO datetimes to `Date`
 *   instances at runtime, so consumers may receive either depending on
 *   transport. Formatters should handle both.
 * - `occurredAt` is `insertDate ?? updateDate` so later non-status saves
 *   cannot rewrite the displayed deny/approve time.
 * - Change-log payloads are intentionally not returned here — only
 *   `changeLogCount` and `hasChangeDetails` flags. Rendering the actual
 *   "See changes" panel is out of scope for this endpoint.
 */
export interface RosterLogEntry {
	id: number;
	status: RosterLogStatus;
	occurredAt: string;
	insertDate: string;
	updateDate: string | null;
	admin: RosterLogAdmin | null;
	adminNotes: string | null;
	changeLogCount: number;
	hasChangeDetails: boolean;
}

/** Fetches the audit log for a roster. Pass `null` to disable the query. */
export function useRosterLogsQuery(rosterId: number | null) {
	return useQuery<
		AxiosResponse<RosterLogEntry[]>,
		AxiosError<CoreApiError>,
		RosterLogEntry[]
	>({
		queryKey: coreApiQueryKey(endpoints.roster.logs(0), 'get', rosterId),
		queryFn: async () =>
			coreApiClient.get<RosterLogEntry[]>(
				endpoints.roster.logs(rosterId as number)
			),
		enabled: rosterId != null,
		select: (response) => response.data,
	});
}
